import React, { useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { round } from 'lodash'
import { FormikNumberInput } from 'particles/NumberInput'
import { LabelSize } from 'particles/Label'
import { COUNTRY_ENUM } from 'services/api/constants'
import { formatCurrencyNumberInput } from 'components/utils'

interface NumberInputProps {
    name: string
    country?: COUNTRY_ENUM | null
    currencySign?: string | null
    isRequired?: boolean
    placeholder: string
    isDisable?: boolean
    decimal?: boolean
    width?: string
    isReadOnly?: boolean
    title?: string
    subTitle?: string
    min?: number
    identifier?: string
    labelSize?: LabelSize
    isCommentable?: boolean
    isCommented?: boolean
}

const NumberInput: React.FC<NumberInputProps> = ({
    country,
    currencySign,
    isRequired = false,
    placeholder,
    isDisable = false,
    isReadOnly = false,
    width,
    decimal,
    min,
    title,
    subTitle,
    identifier,
    labelSize,
    isCommentable = false,
    isCommented = false,
    ...props
}) => {
    const [field, meta] = useField(props.name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const formattedValue = useMemo(() => {
        return formatCurrencyNumberInput({
            value: field.value,
            country,
            currencySign,
            decimal,
        })
    }, [country, decimal, field.value, currencySign])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue: number | null = +e.target.value
        if (Number.isNaN(inputValue)) return
        if (!e.target.value && e.target.value !== '0') {
            inputValue = null
        }
        if (decimal && inputValue) {
            const value = round(inputValue, 2)
            inputValue = value
        }
        setFieldValue(props.name, inputValue)
        if (!meta.touched) setFieldTouched(field.name, true, false)
    }

    return (
        <FormikNumberInput
            {...props}
            name={props.name}
            onChange={onChange}
            size={'m'}
            label={title}
            labelSize={labelSize ? labelSize : 'm'}
            subtext={subTitle}
            isReadOnly={isReadOnly}
            error={meta.touched ? meta.error : undefined}
            prefix={currencySign}
            placeholder={placeholder}
            isRequired={isRequired}
            formattedValue={isReadOnly ? formattedValue : undefined}
            value={field.value}
            min={min}
            isDisabled={isDisable}
            width={width}
            identifier={identifier}
            isCommentable={isCommentable}
            isCommented={isCommented}
        />
    )
}

export default NumberInput
